import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { usePartsAndLocations } from "@/hooks/usePartsAndLocations";
import { apiFetch } from "@/utils/api";

interface TxRow {
  id: string;
  part_name: string;
  part_number: string;
  type_name: string;
  quantity_change: number;
  notes: string;
  user_name: string;
  created_at: string;
}

interface TxType {
  id: string;
  name: string;
  direction: string;
}

export function TransactionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = searchParams.get("new") === "1";
  const [transactions, setTransactions] = useState<TxRow[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { parts, locations, loading: refsLoading } = usePartsAndLocations();
  const [txTypes, setTxTypes] = useState<TxType[]>([]);

  const load = async (q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    const data = await apiFetch<{ transactions: TxRow[] }>(`/transactions${qs}`);
    setTransactions(data.transactions);
  };

  useEffect(() => {
    (async () => {
      try {
        if (!isNew) await load();
        const types = await apiFetch<{ transactionTypes: TxType[] }>("/transaction-types");
        setTxTypes(types.transactionTypes);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew]);

  if (loading || refsLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (isNew) {
    return (
      <NewTransactionForm
        parts={parts}
        locations={locations}
        txTypes={txTypes}
        onCancel={() => setSearchParams({})}
        onCreated={() => {
          setSearchParams({});
          load().catch(() => {});
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Transaction History"
        actions={
          <Button asChild className="bg-[#1e3a4f] hover:bg-[#163041]">
            <Link to="/transactions?new=1">New Transaction</Link>
          </Button>
        }
      />
      <form
        className="flex gap-2 max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          load(search).catch(() => {});
        }}
      >
        <Input placeholder="Search parts or notes…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button type="submit" variant="secondary">Search</Button>
      </form>
      <DataTable
        columns={[
          { key: "date", header: "Date", render: (t) => new Date(t.created_at).toLocaleString() },
          { key: "part", header: "Part", render: (t) => t.part_name },
          { key: "type", header: "Type", render: (t) => t.type_name },
          {
            key: "qty",
            header: "Qty",
            className: "text-right tabular-nums",
            render: (t) => (
              <span className={t.quantity_change >= 0 ? "text-green-700" : "text-red-700"}>
                {t.quantity_change >= 0 ? "+" : ""}{t.quantity_change}
              </span>
            ),
          },
          { key: "user", header: "User", render: (t) => t.user_name || "—" },
          { key: "notes", header: "Notes", render: (t) => t.notes || "—" },
        ]}
        rows={transactions}
        rowKey={(t) => t.id}
      />
    </div>
  );
}

function NewTransactionForm({
  parts,
  locations,
  txTypes,
  onCancel,
  onCreated,
}: {
  parts: { id: string; part_name: string }[];
  locations: { id: string; name: string }[];
  txTypes: TxType[];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [transactionTypeId, setTransactionTypeId] = useState("");
  const [partId, setPartId] = useState("");
  const [quantityChange, setQuantityChange] = useState(1);
  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [notes, setNotes] = useState("");
  const [serialNotes, setSerialNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const selectedType = txTypes.find((t) => t.id === transactionTypeId);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const qty =
        selectedType?.direction === "out"
          ? -Math.abs(quantityChange)
          : selectedType?.direction === "in"
            ? Math.abs(quantityChange)
            : quantityChange;
      await apiFetch("/transactions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transactionTypeId,
          partId,
          quantityChange: qty,
          fromLocationId: fromLocationId || undefined,
          toLocationId: toLocationId || undefined,
          notes,
          serialNotes,
        }),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="New Transaction" />
      <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-lg">
        <div className="space-y-1.5">
          <Label>Transaction Type</Label>
          <Select value={transactionTypeId} onChange={(e) => setTransactionTypeId(e.target.value)}>
            <option value="">Select type…</option>
            {txTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Part</Label>
          <Select value={partId} onChange={(e) => setPartId(e.target.value)}>
            <option value="">Select part…</option>
            {parts.map((p) => (
              <option key={p.id} value={p.id}>{p.part_name}</option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Quantity</Label>
          <Input type="number" min={1} value={quantityChange} onChange={(e) => setQuantityChange(Number(e.target.value))} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>From Location</Label>
            <Select value={fromLocationId} onChange={(e) => setFromLocationId(e.target.value)}>
              <option value="">—</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>To Location</Label>
            <Select value={toLocationId} onChange={(e) => setToLocationId(e.target.value)}>
              <option value="">—</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
        <div className="space-y-1.5">
          <Label>Serial Notes</Label>
          <Input value={serialNotes} onChange={(e) => setSerialNotes(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#1e3a4f] hover:bg-[#163041]">
            {submitting ? "Saving…" : "Create Transaction"}
          </Button>
        </div>
      </div>
    </div>
  );
}
